import { createIdentificationQuery } from '@festina-life/api'
import type { ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { useHook } from '../lenteHooks'
import { Provider } from './idMapperContext'

type Props = {
  children: ReactNode
  ids: string[]
}

const IdMapperInnerProvider = ({ ids, children }: Props) => {
  const useTenantParam = useHook('routing.tenant.useTenantParam')
  const useFetchPersonMap = useHook('fetchers.crm.useFetchPersonMap')
  const useFetchCompanyMap = useHook('fetchers.crm.useFetchCompanyMap')
  const useFetchIdentificationMap = useHook(
    'fetchers.crm.useFetchIdentificationMap',
  )
  const tenantId = useTenantParam()

  const uniqueIds = Array.from(new Set(ids))

  const requestBody = createIdentificationQuery(uniqueIds)
  const { data: personMap } = useFetchPersonMap({
    internalIds: uniqueIds,
    tenantId,
  })
  const { data: companyMap } = useFetchCompanyMap({
    internalIds: uniqueIds,
    tenantId,
  })
  const { data: identificationMap } = useFetchIdentificationMap(
    requestBody
      ? {
          requestBody,
          tenantId,
        }
      : null,
  )

  const getPersonEntity = (internalId: string) => {
    const entityData = personMap.find(
      ({ secondaryId }) => secondaryId === internalId,
    )

    if (!entityData) {
      throw new Error(`Unable to locate Person ${internalId} in Legal Entity`)
    }

    return entityData
  }

  const getCompanyEntity = (internalId: string) => {
    const entityData = companyMap.find(
      ({ secondaryId }) => secondaryId === internalId,
    )

    if (!entityData) {
      throw new Error(`Unable to locate Company ${internalId} in Legal Entity`)
    }

    return entityData
  }

  const getIdentification = (internalId: string) => {
    const identificationData = identificationMap?.legalEntityMaps?.find(
      (legalEntityMap) => legalEntityMap?.legalEntityId === internalId,
    )

    if (!identificationData) {
      throw new Error(`Unable to locate ID ${internalId} in Legal Entity`)
    }

    return identificationData
  }

  return (
    <Provider value={{ getPersonEntity, getCompanyEntity, getIdentification }}>
      {children}
    </Provider>
  )
}

export { IdMapperProvider }

/**
 * Used for providing an Identification array, and then fetches entity and identification data.
 * This can be used for, for example to get a full name from the entity map and SSN from the identification map.
 *
 * The provider may not end up being mounted if any of the fetches fail.
 */
const IdMapperProvider = ({ ids, children }: Props) => (
  <ErrorBoundary fallback={children}>
    {/* We would rather have individual uses of the context fail over the entire child being missing */}
    <IdMapperInnerProvider ids={ids}>{children}</IdMapperInnerProvider>
  </ErrorBoundary>
)
